'use client'

import { useState, useEffect } from 'react'
import Image from 'next/image'
import * as m from 'motion/react-m'
import { AnimatePresence } from 'motion/react'
import { Motion } from '@/lib/motion/lazy-motion'

import Link from '@/components/ui/Link'
import Pill from '@/components/ui/Pill'
import Overlay from '@/components/search/common/Overlay'
import AuthModal from '@/components/authentication/AuthModal'

import { Icon } from '@/icons/Icon'
import { Deal, App } from '@/payload-types'
import { DealPromoCode } from './DealClaim'
import { cn } from '@/lib/utils'
import { fetchCurrentUser } from '@/lib/auth/fetch-current-user'
import { submitForm } from '@/lib/actions/submit-form'

type DealTrackerProps = {
  deal: Deal
  app?: Pick<App, 'id' | 'slug' | 'name' | 'affiliateLink' | 'partnershipTier'>
  close: () => void
}

const mask =
  'radial-gradient(circle at 0 50%, transparent 8px, #09090B 9px) left / 51% 100% no-repeat, radial-gradient(circle at 100% 50%, transparent 8px, #09090B 9px) right / 51% 100% no-repeat'

export default function DealTracker({ deal, app, close }: DealTrackerProps) {
  const detailsLink = app ? `/deals/${app.slug}` : undefined
  const emailIsRequired = !!deal.emailIsRequired
  const hasPromoCode = !!deal.promoCode
  const hasLoopsId = !!deal.transactionalEmailId

  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null)
  const [showAuth, setShowAuth] = useState(false)

  useEffect(() => {
    fetchCurrentUser({ refresh: false }).then((user) => {
      setIsLoggedIn(!!user)
      // Fire-and-forget Loops email for logged-in users; server-side Redis dedup
      // prevents duplicates if the claim flow already sent it.
      if (user && hasLoopsId) {
        submitForm({
          type: 'deal',
          email: user.email,
          pageUrl: window.location.href,
          dealId: deal.id,
        }).catch(() => {})
      }
    })
  }, [deal.id, hasLoopsId])

  const authResolved = isLoggedIn !== null
  const isScenarioA = authResolved && emailIsRequired && !isLoggedIn

  // Subtitle depends on auth for emailIsRequired/Loops deals; show skeleton while loading
  const subtitleNeedsAuth = emailIsRequired || hasLoopsId

  function loggedInSubtitle(): string | null {
    if (hasPromoCode && hasLoopsId)
      return 'Use the link you opened, then enter this promo code at checkout. We also sent the deal details to your email.'
    if (hasPromoCode) return 'Use the link you opened, then enter this promo code at checkout.'
    if (hasLoopsId) return 'We sent the deal details to your email.'
    return "You're all set. Complete your sign-up using the deal link you opened."
  }

  const subtitle = isScenarioA
    ? 'Create a free account to reveal and copy this promo code.'
    : isLoggedIn || !subtitleNeedsAuth
      ? loggedInSubtitle()
      : null

  const showBottomSection = authResolved && (hasPromoCode || !!detailsLink || isScenarioA)

  const redirectUrl = detailsLink
    ? `${detailsLink}?claim=true`
    : `${typeof window !== 'undefined' ? window.location.pathname : ''}?claim=true`

  return (
    <>
      <Motion>
        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="fixed bottom-0 right-0 z-50 w-full sm:bottom-6 sm:right-6 sm:max-w-80"
        >
          <div
            className={cn(
              'relative z-40 flex flex-col items-center rounded-t-2xl bg-gradient-to-b from-zinc-800 to-zinc-950 px-8 pt-8 before:pointer-events-none before:absolute before:inset-0 before:rounded-t-2xl before:border-x before:border-t before:border-white/10',
              showBottomSection ? 'pb-6' : 'pb-8 sm:rounded-b-2xl sm:before:rounded-b-2xl',
            )}
          >
            <div
              onClick={close}
              className="absolute right-2.5 top-2.5 z-50 flex h-7 w-7 cursor-pointer items-center justify-center rounded-md bg-white/10 text-white transition-colors hover:bg-white/20"
            >
              <Icon name="xmark" size={14} />
            </div>

            <Image
              src="/img/global/confirmation.png"
              alt="Deal tracked!"
              width={48}
              height={48}
              className="mb-6"
            />
            <div className="flex flex-col items-center gap-3 text-center">
              <span className="text-20 font-semibold leading-none text-white">Deal tracked!</span>
              {subtitleNeedsAuth && !authResolved ? (
                <div className="h-8 w-48 animate-pulse rounded-md bg-white/10" />
              ) : subtitle ? (
                <p className="max-w-60 text-13 leading-normal text-zinc-400">{subtitle}</p>
              ) : null}
            </div>
          </div>

          {showBottomSection && (
            <>
              <div
                className="relative hidden h-4 w-full items-center justify-center bg-primary-500 px-2 before:absolute before:left-0 before:h-4 before:w-2 before:rounded-r-full before:border-r before:border-zinc-800 after:absolute after:right-0 after:h-4 after:w-2 after:rounded-l-full after:border-l after:border-zinc-800 sm:flex"
                style={{ background: mask }}
              >
                <div className="h-[3px] w-full border-t-[3px] border-dotted border-zinc-700"></div>
              </div>

              <div className="relative rounded-b-2xl bg-zinc-950 px-6 pb-8 pt-4 before:absolute before:inset-0 before:z-0 before:rounded-b-2xl before:border-x before:border-b before:border-white/10 sm:pb-6">
                {isScenarioA ? (
                  // Scenario A: not logged in + emailIsRequired
                  <div
                    className={cn(
                      'relative flex w-full flex-col',
                      hasPromoCode ? 'gap-10 sm:gap-8' : 'gap-5',
                    )}
                  >
                    <button
                      className="cursor-pointer"
                      onClick={() => {
                        try {
                          localStorage.setItem('efficient:deal_id', String(deal.id))
                          localStorage.setItem('efficient:claim', 'true')
                        } catch {
                          // ignore storage failures; still route to sign-in
                        }
                        setShowAuth(true)
                      }}
                    >
                      {hasPromoCode && (
                        <DealPromoCode
                          promoCode={deal.promoCode!}
                          fullWidth={true}
                          highlight={true}
                          locked={true}
                        />
                      )}
                      <div
                        className={cn(
                          'z-40 flex w-full justify-center',
                          hasPromoCode ? 'absolute left-1/2 top-6 -translate-x-1/2' : 'relative',
                        )}
                      >
                        <div
                          className={cn(
                            'relative flex items-center justify-center gap-1.5 rounded-full border border-emerald-500/60 bg-gradient-to-r from-emerald-500/30 to-black pl-2.5 pr-3 text-emerald-500 backdrop-blur-md transition-colors hover:border-emerald-500',
                            hasPromoCode ? 'h-8 w-fit text-12' : 'h-10 w-full text-13',
                          )}
                        >
                          <Icon name="lock" size={12} />
                          Create free account to view code
                        </div>
                      </div>
                    </button>
                    {detailsLink && (
                      <Link
                        href={detailsLink}
                        className="z-40 mx-auto w-fit text-center text-13 leading-normal text-zinc-400 underline underline-offset-2 transition-colors hover:text-white"
                      >
                        Details and terms
                      </Link>
                    )}
                  </div>
                ) : (
                  // Logged-in (or no emailIsRequired): hasPromoCode × hasLoopsId matrix
                  <div className="flex w-full flex-col gap-6 sm:gap-3">
                    {hasPromoCode && (
                      <DealPromoCode
                        promoCode={deal.promoCode!}
                        fullWidth={true}
                        highlight={true}
                      />
                    )}
                    {detailsLink && (
                      <Link href={detailsLink} className="relative z-40 w-full">
                        <Pill size="large" style="subtle" theme="dark" className="w-full text-14">
                          View deal details
                        </Pill>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </m.div>
      </Motion>

      <Overlay close={close} background={true} z={40} className="sm:hidden" />

      <AnimatePresence>
        {showAuth && (
          <AuthModal
            close={() => setShowAuth(false)}
            redirectUrl={redirectUrl}
            config={{ signUp: true }}
          />
        )}
      </AnimatePresence>
    </>
  )
}
